import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import { useTranslation } from '@/hooks/useTranslation';
import { hasPermission, isSuperAdmin } from '@/lib/permissions';
import { buildLoginUrlWithRedirect } from '@/lib/redirectTo';
import { AlertCircle } from 'lucide-react';
import React from 'react';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If provided, user must have this specific permission to access the route */
  requiredPermission?: {
    entity: string;
    action: string;
  };
}

/**
 * ProtectedRoute component that handles authentication and authorization routing logic
 *
 * If the current URL is public, it renders the children directly.
 * If the current URL is private and the user is authenticated, it checks permissions (if required).
 * If the user doesn't have required permissions, it shows an access denied message.
 * If the current URL is private and the user is not authenticated, it redirects to the login page.
 *
 * @param children - The child components to render if the route is accessible
 * @param requiredPermission - Optional permission requirement for the route
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { location, isPublicRoute } = useCurrentRoute();

  // While authentication state is loading, show loading indicator
  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
          <p className='mt-2 text-muted-foreground'>{t('auth.loading')}</p>
        </div>
      </div>
    );
  }

  // Allow access to public routes regardless of authentication
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // For private routes, ensure the user is authenticated
  if (!isAuthenticated) {
    // Redirect to login while preserving the intended destination
    return (
      <Navigate
        to={buildLoginUrlWithRedirect(location)}
        state={{ from: location }}
        replace
      />
    );
  }

  // If a specific permission is required, check it
  if (requiredPermission) {
    const { entity, action } = requiredPermission;

    // Check if user has the required permission or is superadmin
    if (!hasPermission(user, entity, action)) {
      return (
        <div className='flex items-center justify-center min-h-screen p-4'>
          <Alert variant='destructive' className='max-w-md'>
            <AlertCircle className='h-4 w-4' />
            <AlertTitle>{t('auth.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('auth.noPermission')}
              {isSuperAdmin(user)
                ? ` ${t('auth.contactAdmin')}`
                : ` ${t('auth.requiredPermission', { entity, action })}`}
            </AlertDescription>
          </Alert>
        </div>
      );
    }
  }

  // User is authenticated and authorized, allow access
  return <>{children}</>;
};
